import {all, call, fork, put, select, takeLatest} from 'redux-saga/effects'
import types from '../actions/types'
import {createPivotTableWatcher} from 'sagas/pivotTable'
import {
  opportunityDialogActivitiesPivotTableKey,
  opportunityDialogQuotesPivotTableKey,
} from 'reducers/dialogs/opportunityDialog'
import {quotePivotTableFields} from 'containers/quotePivotTable'
import {fullDiaryFields} from 'sagas/diarySaga'
import {generalError} from 'actions/index'
import {
  addOpportunityFailure,
  addOpportunitySuccess,
  loadOpportunityFailure,
  loadOpportunitySuccess,
  updateOpportunityFailure,
  updateOpportunitySuccess,
} from 'actions/opportunities'
import {
  loadLeadsOpportunities as loadLeadsOpportunitiesAction,
  loadLeadsOpportunitiesFailure,
  loadLeadsOpportunitiesSuccess,
  loadSellingOpportunities as loadSellingOpportunitiesAction,
  loadSellingOpportunitiesFailure,
  loadSellingOpportunitiesSuccess,
  loadSuccessStats as loadSuccessStatsAction,
  loadSuccessStatsFailure,
  loadSuccessStatsSuccess,
  loadWonOpportunities as loadWonOpportunitiesAction,
  loadWonOpportunitiesFailure,
  loadWonOpportunitiesSuccess,
  updateLeadsOpportunity,
  updateSellingOpportunity,
  updateUserOrTeam,
  updateWonOpportunity,
} from 'actions/sales'
import {getOpportunityDialog} from 'selectors/dialog'
import {get, isEmpty, isNil} from 'lodash'
import {showOpportunityDialog} from 'actions/dialog'
import {salesContextOpportunitiesPivotTableKey} from 'reducers/salesContext/opportunitiesView'
import {getInvoiceFinancialYearStart} from 'selectors/settings'
import {
  getJobTypes,
  getLeadsFilters,
  getLeadsOpportunities,
  getLeadsSearch,
  getOpportunitiesView,
  getSellingFilters,
  getSellingOpportunities,
  getSellingSearch,
  getShowDormants,
  getSuccessStatsFilters,
  getUserOrTeam,
  getWonFilters,
  getWonOpportunities,
  getWonSearch,
  isAnySuccessStatsVisible,
} from 'selectors/salesContext'
import {getDateFilters} from 'helpers/dateRange'
import {getSearch} from 'selectors/pivotTable'
import {
  findLeadsOpportunity,
  findSellingOpportunity,
  findWonOpportunity,
  getLeadsParams,
  getOpportunityFilterParams,
  getSellingParams,
  getSellingStages,
  getSuccessStatsParams,
  getWonParams,
  opportunityFitsLeadsFilters,
  opportunityFitsSellingFilters,
  opportunityFitsWonFilters,
} from 'helpers/opportunities'
import {
  getHomeContacts,
  getOpportunityStages,
  getTeamMembers,
  getTeams,
} from 'selectors/data'
import {
  getContactsForTeamOrUserOption, getContactsForSalesAnalysisTeamOrUserOptions,
  getTeamOrUserOption, getTeamOrUserOptions,
  getTeamsAndUsersOptions,
  UnallocatedTeamOption,
} from 'helpers/teams'
import {getUser, getUserSettings} from 'selectors/auth'
import {isLeadsBoardVisible, isSellingBoardVisible, isWonBoardVisible} from 'selectors/routing'
import {takeLatestWithKey} from 'helpers/saga'
import {requestData, reset} from 'actions/pivotTable'
import {salesContextSalesAnalysisQuotesViewPivotTableKey} from 'reducers/salesContext/salesAnalysisView/quoteDataSource'
import {getQuotingFilterParams} from 'helpers/quotes'
import {filterDormantContacts} from 'helpers/contacts'
import {getFiltersForDataSource, getSalesAnalysisUsersFilterOptions} from 'helpers/sales'


const KANBAN_PAGE_SIZE = 25

const salesAnalysisQuoteFields = {
  ...quotePivotTableFields,
  contact: {
    ...quotePivotTableFields.contact,
    userfields: true,
  },
}

export const opportunityFields = {
  default: true,
  company: {
    id: true,
    name: true,
  },
  contact: {
    id: true,
    companyId: true,
    forename: true,
    surname: true,
    mobile: true,
    phone: true,
    email: true,
    emailValid: true,
    location: true,
    hubspotContactMapping: true,
  },
  salesman: {
    id: true,
    companyId: true,
    forename: true,
    surname: true,
  },
  creator: {
    id: true,
    companyId: true,
    forename: true,
    surname: true,
  },
  stage: true,
  lastActivity: {
    id: true,
    modifiedDate: true,
  },
  nextActivity: {
    id: true,
    startDate: true,
    activitytype: true,
  },
  history: {
    default: true,
    contact: true,
  },
  orders: {
    id: true,
    status: true,
  },
  hubspotDealMapping: true,
}

export default (api) => {

  function* loadOpportunity({opportunityId}) {
    try {
      const params = {
        id: opportunityId,
        summary: true,
      }

      yield put(reset(opportunityDialogActivitiesPivotTableKey))
      yield put(reset(opportunityDialogQuotesPivotTableKey))
      yield put(showOpportunityDialog(false))

      const response = yield call(api.opportunity.get, params, opportunityFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const opportunity = data.result[0]
            yield put(loadOpportunitySuccess(opportunity))
            yield put(requestData(opportunityDialogActivitiesPivotTableKey))
          },
          function* onFail(key, message, code) {
            yield put(loadOpportunityFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addOpportunity({opportunity}) {
    try {
      const params = {
        opportunity,
        summary: true,
      }

      const response = yield call(api.opportunity.add, params, opportunityFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const updatedOpportunity = data.result
            yield put(addOpportunitySuccess(updatedOpportunity))
          },
          function* onFail(key, message, code) {
            yield put(addOpportunityFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updateOpportunity({opportunity}) {
    try {
      const params = {
        opportunity,
        summary: true,
      }

      // Optimistic update of Kanban
      yield call(opportunityUpdated, opportunity)

      const response = yield call(api.opportunity.update, params, opportunityFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const updatedOpportunity = data.result
            yield put(updateOpportunitySuccess(updatedOpportunity))
          },
          function* onFail(key, message, code) {
            yield put(updateOpportunityFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* reloadOpportunitiesIfVisible() {
    try {
      yield call(callIfBoardsVisible, reloadLeadsOpportunities, reloadSellingOpportunities, reloadWonOpportunities)

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* reloadLeadsOpportunities() {
    try {
      const user = yield select(getUser)
      const teams = yield select(getTeams)
      const teamMembers = yield select(getTeamMembers)
      const showDormants = yield select(getShowDormants)
      const homeContacts = yield select(state => getHomeContacts(state, showDormants))
      const userOrTeam = yield select(getUserOrTeam)

      const teamUserOptions = getTeamsAndUsersOptions(user, teams, teamMembers, homeContacts, 'Leads')
      const teamOrUserOption = getTeamOrUserOption(user, teams, teamMembers, teamUserOptions, userOrTeam)
      const contactsToLoad = getContactsForTeamOrUserOption(teamOrUserOption, teams, teamMembers, homeContacts)

      yield all([
        put(loadLeadsOpportunitiesAction()),
        ...contactsToLoad.map(contact =>
            put(loadLeadsOpportunitiesAction(contact.id))
        ),
      ])

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadLeadsOpportunities({ownerId, page}) {
    try {
      const invoiceFinancialYearStart = yield select(getInvoiceFinancialYearStart)
      const filters = yield select(getLeadsFilters)
      const jobTypes = yield select(getJobTypes)
      const search = yield select(getLeadsSearch)
      const params = getLeadsParams(ownerId, filters, jobTypes, search, invoiceFinancialYearStart, page, KANBAN_PAGE_SIZE)

      const response = yield call(api.opportunity.get, params, opportunityFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadLeadsOpportunitiesSuccess(ownerId, data.result, data.count, data.value, page))
          },
          function* onFail(key, message, code) {
            yield put(loadLeadsOpportunitiesFailure(message, code))
          }
      )

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* reloadSellingOpportunities() {
    try {
      const opportunityStages = yield select(getOpportunityStages)
      const sellingStages = getSellingStages(opportunityStages)

      yield all(sellingStages.map(({id: stageId}) =>
          put(loadSellingOpportunitiesAction(stageId))
      ))

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updateSellingBoardTeamFilter({contactId}) {
    const user = yield select(getUser)
    if (user.contactId === contactId) {
      const userOrTeam = yield select(getUserOrTeam)
      if (userOrTeam === UnallocatedTeamOption.value) {
        yield put(updateUserOrTeam(null))
      }
    }
  }

  function* loadSellingOpportunities({stageId, page}) {
    try {
      const user = yield select(getUser)
      const invoiceFinancialYearStart = yield select(getInvoiceFinancialYearStart)
      const filters = yield select(getSellingFilters)
      const search = yield select(getSellingSearch)
      const showDormants = yield select(getShowDormants)
      const teams = yield select(getTeams)
      const teamMembers = yield select(getTeamMembers)
      const homeContacts = yield select(state => getHomeContacts(state, true))
      const userOrTeam = yield select(getUserOrTeam)
      const jobTypes = yield select(getJobTypes)

      const teamUserOptions = getTeamsAndUsersOptions(user, teams, teamMembers, homeContacts, 'Leads', true)
      const teamOrUserOption = getTeamOrUserOption(user, teams, teamMembers, teamUserOptions, userOrTeam)

      const params = getSellingParams(
          stageId,
          filters,
          teamOrUserOption,
          search,
          teams,
          teamMembers,
          homeContacts,
          jobTypes,
          invoiceFinancialYearStart,
          showDormants,
          page,
          KANBAN_PAGE_SIZE
      )

      const response = yield call(api.opportunity.get, params, opportunityFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadSellingOpportunitiesSuccess(stageId, data.result, data.count, data.value, page))
          },
          function* onFail(key, message, code) {
            yield put(loadSellingOpportunitiesFailure(stageId, message, code))
          }
      )

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* reloadWonOpportunities() {
    try {
      const user = yield select(getUser)
      const teams = yield select(getTeams)
      const teamMembers = yield select(getTeamMembers)
      const showDormants = yield select(getShowDormants)
      const homeContacts = yield select(state => getHomeContacts(state, showDormants))
      const userOrTeam = yield select(getUserOrTeam)

      const teamUserOptions = getTeamsAndUsersOptions(user, teams, teamMembers, homeContacts, 'Opportunities')
      const teamOrUserOption = getTeamOrUserOption(user, teams, teamMembers, teamUserOptions, userOrTeam)
      const contactsToLoad = getContactsForTeamOrUserOption(teamOrUserOption, teams, teamMembers, homeContacts)

      yield all([
        put(loadWonOpportunitiesAction()),
        ...contactsToLoad.map(contact =>
            put(loadWonOpportunitiesAction(contact.id))
        ),
      ])

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadWonOpportunities({ownerId, page}) {
    try {
      const invoiceFinancialYearStart = yield select(getInvoiceFinancialYearStart)
      const filters = yield select(getWonFilters)
      const jobTypes = yield select(getJobTypes)
      const search = yield select(getWonSearch)
      const params = getWonParams(ownerId, filters, jobTypes, search, invoiceFinancialYearStart, page, KANBAN_PAGE_SIZE)

      const response = yield call(api.opportunity.get, params, opportunityFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadWonOpportunitiesSuccess(ownerId, data.result, data.count, data.value, page))
          },
          function* onFail(key, message, code) {
            yield put(loadWonOpportunitiesFailure(message, code))
          }
      )

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* saveReceived({data}) {
    try {
      const updatedOpportunity = data.result
      yield call(opportunityUpdated, updatedOpportunity)

      const successStatsVisible = yield select(isAnySuccessStatsVisible)
      if (successStatsVisible) yield put(loadSuccessStatsAction())
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* opportunityUpdated(opportunity) {
    yield call(callIfBoardsVisible, leadOpportunityUpdated, sellingOpportunityUpdated, wonOpportunityUpdated, opportunity)
  }

  function* callIfBoardsVisible(callForLeads, callForSelling, callForWon, ...additionalParams) {
    const leadsBoardVisible = yield select(isLeadsBoardVisible)
    const sellingBoardVisible = yield select(isSellingBoardVisible)
    const wonBoardVisible = yield select(isWonBoardVisible)

    if (leadsBoardVisible) yield call(callForLeads, ...additionalParams)
    if (sellingBoardVisible) yield call(callForSelling, ...additionalParams)
    if (wonBoardVisible) yield call(callForWon, ...additionalParams)
  }

  function* leadOpportunityUpdated(opportunity) {
    const leadsOpportunities = yield select(getLeadsOpportunities)
    const previousOpportunity = findLeadsOpportunity(leadsOpportunities, opportunity.id)
    const existsPreviously = !isNil(previousOpportunity)

    const user = yield select(getUser)
    const filters = yield select(getLeadsFilters)
    const search = yield select(getLeadsSearch)
    const invoiceFinancialYearStart = yield select(getInvoiceFinancialYearStart)
    const showDormants = yield select(getShowDormants)
    const teams = yield select(getTeams)
    const teamMembers = yield select(getTeamMembers)
    const homeContacts = yield select(getHomeContacts, true)

    const existsNow = opportunityFitsLeadsFilters(
        user,
        opportunity,
        filters,
        search,
        invoiceFinancialYearStart,
        showDormants,
        teams,
        teamMembers,
        homeContacts
    )

    if (existsNow || existsPreviously) {
      yield put(updateLeadsOpportunity(previousOpportunity, existsNow ? opportunity : null))
    }
  }

  function* sellingOpportunityUpdated(opportunity) {
    const sellingOpportunities = yield select(getSellingOpportunities)
    const previousOpportunity = findSellingOpportunity(sellingOpportunities, opportunity.id)
    const existsPreviously = !isNil(previousOpportunity)

    const user = yield select(getUser)
    const filters = yield select(getSellingFilters)
    const search = yield select(getSellingSearch)
    const opportunityStages = yield select(getOpportunityStages)
    const invoiceFinancialYearStart = yield select(getInvoiceFinancialYearStart)
    const showDormants = yield select(getShowDormants)
    const teams = yield select(getTeams)
    const teamMembers = yield select(getTeamMembers)
    const homeContacts = yield select(getHomeContacts, true)
    const userOrTeam = yield select(getUserOrTeam)

    const existsNow = opportunityFitsSellingFilters(
        user,
        opportunity,
        filters,
        userOrTeam,
        search,
        opportunityStages,
        invoiceFinancialYearStart,
        showDormants,
        teams,
        teamMembers,
        homeContacts
    )

    if (existsNow || existsPreviously) {
      yield put(updateSellingOpportunity(previousOpportunity, existsNow ? opportunity : null))
    }
  }

  function* wonOpportunityUpdated(opportunity) {
    const wonOpportunities = yield select(getWonOpportunities)
    const previousOpportunity = findWonOpportunity(wonOpportunities, opportunity.id)
    const existsPreviously = !isNil(previousOpportunity)

    const user = yield select(getUser)
    const filters = yield select(getWonFilters)
    const search = yield select(getWonSearch)
    const invoiceFinancialYearStart = yield select(getInvoiceFinancialYearStart)
    const showDormants = yield select(getShowDormants)
    const teams = yield select(getTeams)
    const teamMembers = yield select(getTeamMembers)
    const homeContacts = yield select(getHomeContacts, true)

    const existsNow = opportunityFitsWonFilters(
        user,
        opportunity,
        filters,
        search,
        invoiceFinancialYearStart,
        showDormants,
        teams,
        teamMembers,
        homeContacts
    )

    if (existsNow || existsPreviously) {
      yield put(updateWonOpportunity(previousOpportunity, existsNow ? opportunity : null))
    }
  }

  function* loadSuccessStats() {
    try {
      const user = yield select(getUser)
      const invoiceFinancialYearStart = yield select(getInvoiceFinancialYearStart)
      const teams = yield select(getTeams)
      const teamMembers = yield select(getTeamMembers)
      const homeContacts = yield select(state => getHomeContacts(state, true))
      const filters = yield select(getSuccessStatsFilters)
      const jobTypes = yield select(getJobTypes)

      const userOrTeam = yield select(getUserOrTeam)
      const teamUserOptions = getTeamsAndUsersOptions(user, teams, teamMembers, homeContacts)
      const teamOrUserOption = getTeamOrUserOption(user, teams, teamMembers, teamUserOptions, userOrTeam)

      const params = getSuccessStatsParams(
          filters,
          teamOrUserOption,
          teams,
          teamMembers,
          homeContacts,
          jobTypes,
          invoiceFinancialYearStart
      )

      const response = yield call(api.opportunity.getSuccessStats, params)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadSuccessStatsSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(loadSuccessStatsFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* reloadActivitiesIfVisible() {
    try {
      const dialogState = yield select(getOpportunityDialog)
      if (dialogState.isVisible)
        yield put(requestData(opportunityDialogActivitiesPivotTableKey))

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* myWatcher() {
    yield all([
      takeLatest(types.OPPORTUNITY_LOAD_ATTEMPT, loadOpportunity),
      takeLatest(types.OPPORTUNITY_ADD_ATTEMPT, addOpportunity),
      takeLatest(types.OPPORTUNITY_UPDATE_ATTEMPT, updateOpportunity),
      takeLatest(types.OPPORTUNITY_SAVE_RECEIVED, saveReceived),

      takeLatestWithKey(types.SALES_LOAD_LEADS_OPPORTUNITIES_ATTEMPT, loadLeadsOpportunities, 'ownerId'),
      takeLatest(types.SALES_UPDATE_LEADS_FILTERS, reloadLeadsOpportunities),
      takeLatest(types.SALES_SEARCH_LEADS_OPPORTUNITIES, reloadLeadsOpportunities),
      takeLatest(types.SALES_LOAD_ALL_LEADS_OPPORTUNITIES, reloadLeadsOpportunities),

      takeLatestWithKey(types.SALES_LOAD_SELLING_OPPORTUNITIES_ATTEMPT, loadSellingOpportunities, 'stageId'),
      takeLatest(types.SALES_UPDATE_SELLING_FILTERS, reloadSellingOpportunities),
      takeLatest(types.SALES_SEARCH_SELLING_OPPORTUNITIES, reloadSellingOpportunities),
      takeLatest(types.SALES_LOAD_ALL_SELLING_OPPORTUNITIES, reloadSellingOpportunities),
      takeLatest(types.SETTINGS_UPDATE_TEAM_MEMBER_ATTEMPT, updateSellingBoardTeamFilter),

      takeLatestWithKey(types.SALES_LOAD_WON_OPPORTUNITIES_ATTEMPT, loadWonOpportunities, 'ownerId'),
      takeLatest(types.SALES_UPDATE_WON_FILTERS, reloadWonOpportunities),
      takeLatest(types.SALES_SEARCH_WON_OPPORTUNITIES, reloadWonOpportunities),
      takeLatest(types.SALES_LOAD_ALL_WON_OPPORTUNITIES, reloadWonOpportunities),

      takeLatest(types.SALES_TOGGLE_DORMANTS, reloadOpportunitiesIfVisible),
      takeLatest(types.SALES_UPDATE_USER_OR_TEAM, reloadOpportunitiesIfVisible),
      takeLatest(types.SALES_UPDATE_JOB_TYPES, reloadOpportunitiesIfVisible),

      takeLatest(types.SALES_LOAD_SUCCESS_STATS_ATTEMPT, loadSuccessStats),
      takeLatest(types.SALES_UPDATE_USER_OR_TEAM, loadSuccessStats),
      takeLatest(types.SALES_UPDATE_JOB_TYPES, loadSuccessStats),
      takeLatest(types.SALES_UPDATE_SUCCESS_STATS_FILTERS, loadSuccessStats),

      takeLatest(types.DIARY_ADD_ACTIVITY_SUCCESS, reloadOpportunitiesIfVisible),
      takeLatest(types.DIARY_EDIT_ACTIVITY_SUCCESS, reloadOpportunitiesIfVisible),
      takeLatest(types.DIARY_DELETE_ACTIVITY_SUCCESS, reloadOpportunitiesIfVisible),
      takeLatest(types.RELOAD_OPPORTUNITY_ON_BOARD, reloadOpportunitiesIfVisible),

      takeLatest(types.DIARY_ADD_ACTIVITY_SUCCESS, reloadActivitiesIfVisible),
      takeLatest(types.DIARY_EDIT_ACTIVITY_SUCCESS, reloadActivitiesIfVisible),
      takeLatest(types.DIARY_DELETE_ACTIVITY_SUCCESS, reloadActivitiesIfVisible),
    ])
  }

  const opportunityActivitiesPivotTable = createPivotTableWatcher(
      api.activity,
      opportunityDialogActivitiesPivotTableKey,
      [],
      state => {
        const {opportunity} = getOpportunityDialog(state)
        const opportunityId = get(opportunity, 'id', null)
        return {
          opportunityId: isNil(opportunityId) ? [] : opportunityId,
          showHiddenTypes: false,
        }
      },
      fullDiaryFields
  )

  const opportunityQuotesPivotTable = createPivotTableWatcher(
      api.quote,
      opportunityDialogQuotesPivotTableKey,
      [],
      state => {
        const {opportunity} = getOpportunityDialog(state)
        const opportunityId = get(opportunity, 'id', null)

        return {
          opportunityId,
        }
      },
      quotePivotTableFields
  )

  const salesOpportunitiesPivotTable = createPivotTableWatcher(
      api.opportunity,
      salesContextOpportunitiesPivotTableKey,
      [
        types.OPPORTUNITY_UPDATE_FILTERS,
        types.OPPORTUNITY_ADD_SUCCESS,
        types.OPPORTUNITY_UPDATE_SUCCESS,
        types.DIARY_ADD_ACTIVITY_SUCCESS,
        types.DIARY_EDIT_ACTIVITY_SUCCESS,
      ],
      state => {
        const invoiceFinancialYearStart = getInvoiceFinancialYearStart(state)
        const filters = getOpportunitiesView(state)
        const {targetStart, targetEnd} = getDateFilters(filters, invoiceFinancialYearStart)
        const {stageId, status, orderReasons, rejectReasons, companies, users, jobTypes, dateTypes} = filters
        const searchTerm = getSearch(state, salesContextOpportunitiesPivotTableKey)

        return getOpportunityFilterParams(
            searchTerm,
            stageId,
            status,
            orderReasons,
            rejectReasons,
            companies,
            users,
            jobTypes,
            dateTypes,
            targetStart,
            targetEnd
        )
      },
      opportunityFields
  )

  const quoteSalesAnalysisPivotTable = createPivotTableWatcher(
      api.quote,
      salesContextSalesAnalysisQuotesViewPivotTableKey,
      [
        types.SALES_UPDATE_SALES_ANALYSIS_QUOTING_FILTERS,
      ],
      state => {
        const filters = getFiltersForDataSource(state)
        const {
          statusTypes,
          companies,
          userTypes,
          showDormants,
          contacts,
          dateTypes,
          includeFullyScheduled,
          orderTypeId,
        } = filters


        const user = getUser(state)
        const teams = getTeams(state)
        const teamMembers = getTeamMembers(state)
        const homeContacts = getHomeContacts(state, true)
        const invoiceFinancialYearStart = getInvoiceFinancialYearStart(state)
        const {targetStart, targetEnd} = getDateFilters(filters, invoiceFinancialYearStart)
        const userSettings = getUserSettings(state)

        const teamUserOptions =
            getSalesAnalysisUsersFilterOptions(user, teams, teamMembers, homeContacts, 'Quotes')
        let teamOrUserOptions = getTeamOrUserOptions(teamUserOptions, contacts)

        if (isEmpty(teamOrUserOptions) && !isEmpty(teamUserOptions))
          teamOrUserOptions = [teamUserOptions[0].value]

        const ownerIds = getContactsForSalesAnalysisTeamOrUserOptions(
            teamOrUserOptions,
            teams,
            teamMembers,
            filterDormantContacts(homeContacts, showDormants)
        )

        return getQuotingFilterParams(
            false,
            getSearch(state, salesContextSalesAnalysisQuotesViewPivotTableKey),
            statusTypes,
            companies,
            userTypes,
            ownerIds,
            dateTypes,
            includeFullyScheduled,
            targetStart,
            targetEnd,
            orderTypeId,
            userSettings)
      },
      salesAnalysisQuoteFields
  )

  function* watcher() {
    yield fork(myWatcher)
    yield fork(opportunityActivitiesPivotTable)
    yield fork(opportunityQuotesPivotTable)
    yield fork(salesOpportunitiesPivotTable)
    yield fork(quoteSalesAnalysisPivotTable)
  }

  return {
    watcher,
  }
}
